import type { DecisionAudit } from '@kkp-fraud/shared';

/**
 * The Monitor's throughput arithmetic, kept out of the component so it can be checked
 * without rendering. Derived only from the decision rows the console already reads — an
 * empty minute has a count of 0 but NO latency (`null`), never a latency of 0.
 */

const MINUTE = 60_000;

function nearestRank(sorted: readonly number[], p: number): number {
  const rank = Math.ceil((p / 100) * sorted.length);
  return sorted[Math.min(sorted.length - 1, Math.max(0, rank - 1))]!;
}

export interface MinuteSeries {
  /** Decisions per minute, oldest first, for the last `minutes` complete minutes. */
  counts: number[];
  /** p95 service latency per minute, `null` where the minute had no decisions. */
  p95: (number | null)[];
}

export function minuteSeries(rows: readonly DecisionAudit[], now: number, minutes = 60): MinuteSeries {
  const end = Math.floor(now / MINUTE); // the current, still-filling minute is left out
  const start = end - minutes;
  const buckets: number[][] = Array.from({ length: minutes }, () => []);
  for (const r of rows) {
    const m = Math.floor(Date.parse(r.eventTimeUtc) / MINUTE);
    if (m >= start && m < end && Number.isFinite(r.serviceLatencyMs)) buckets[m - start]!.push(r.serviceLatencyMs);
  }
  return {
    counts: buckets.map((b) => b.length),
    p95: buckets.map((b) => (b.length ? nearestRank([...b].sort((x, y) => x - y), 95) : null)),
  };
}

export interface Throughput {
  /** Decisions per second over the last five complete minutes. */
  tpsNow: number;
  /** The busiest minute in the rows' window, as decisions per second. */
  tpsPeak: number;
  /** When that busiest minute started. */
  peakAt: string | null;
}

export function throughput(rows: readonly DecisionAudit[], now: number): Throughput {
  const end = Math.floor(now / MINUTE);
  const perMinute = new Map<number, number>();
  for (const r of rows) {
    const m = Math.floor(Date.parse(r.eventTimeUtc) / MINUTE);
    if (m < end) perMinute.set(m, (perMinute.get(m) ?? 0) + 1);
  }
  let recent = 0;
  for (let m = end - 5; m < end; m++) recent += perMinute.get(m) ?? 0;
  let peak = 0;
  let peakMinute: number | null = null;
  for (const [m, n] of perMinute) {
    if (n > peak || (n === peak && peakMinute !== null && m > peakMinute)) {
      peak = n;
      peakMinute = m;
    }
  }
  return {
    tpsNow: recent / 300,
    tpsPeak: peak / 60,
    peakAt: peakMinute === null ? null : new Date(peakMinute * MINUTE).toISOString(),
  };
}

/** One row of the Monitor's dependency list. */
export interface Dependency {
  id: 'decision-api' | 'redis' | 'cosmos' | 'event-hubs' | 'key-vault' | 'state-store';
  name: string;
  up: boolean;
  /** Round trip, when something measured it. */
  latencyMs: number | null;
}

/** What `/readyz` actually reports — nothing is listed that it does not vouch for. */
export function dependenciesFromReadiness(
  ready: { status: string; stateStore: string; stateStoreOk: boolean; rulesSource: string } | null,
  rttMs: number | null,
): Dependency[] {
  if (!ready) return [{ id: 'decision-api', name: 'Decision API', up: false, latencyMs: null }];
  const deps: Dependency[] = [
    { id: 'decision-api', name: 'Decision API', up: ready.status === 'ready', latencyMs: rttMs },
    {
      id: ready.stateStore === 'redis' ? 'redis' : 'state-store',
      name: ready.stateStore === 'redis' ? 'Redis' : ready.stateStore,
      up: ready.stateStoreOk,
      latencyMs: null,
    },
  ];
  if (ready.rulesSource === 'cosmos') deps.push({ id: 'cosmos', name: 'Cosmos DB', up: true, latencyMs: null });
  return deps;
}
